import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";

const BACKEND_URL =
  window.location.hostname === "localhost"
    ? process.env.REACT_APP_BACKEND_URL_LOCAL
    : process.env.REACT_APP_BACKEND_URL_PROD;

export default function ProduitsForm({ open, setOpen, produitSelectionne, fetchProduits }) {
  const [formValues, setFormValues] = useState({
    nom_produit: "",
    code_isin: "",
    societe_gestion: "",
    description: "",
    site_web: "",
    objectif: "",
    indice_reference: "",
    type: "",
    risque: "",
    duree_recommandee: "",
    depositaire: "",
    commentaires: "",
    lien_dic: "",
    allocation_taux: "",
    allocation_actions: "",
    allocation_opc: "",
    allocation_convertibles: "",
    derives: "",
    sensibilite_taux: "",
  });

  const [notification, setNotification] = useState({ show: false, message: "", type: "success" });

  const [societes, setSocietes] = useState([]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/partenaires/noms-societes`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setSocietes(data);
        } else {
          console.error("❌ La réponse n'est pas un tableau :", data);
          setSocietes([]);
        }
      })
      .catch((err) => {
        console.error("Erreur lors du chargement des sociétés :", err);
        setSocietes([]);
      });
  }, []);   

  useEffect(() => {
    if (produitSelectionne) {
      setFormValues({
        ...produitSelectionne,
        allocation_taux: produitSelectionne.strategie?.allocation?.taux || "",
        allocation_actions: produitSelectionne.strategie?.allocation?.actions || "",
        allocation_opc: produitSelectionne.strategie?.allocation?.opc || "",
        allocation_convertibles: produitSelectionne.strategie?.allocation?.convertibles || "",
        derives: produitSelectionne.strategie?.derives || "",
        sensibilite_taux: produitSelectionne.strategie?.sensibilite_taux || "",
      });
    }
  }, [produitSelectionne]);

  const showToast = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "success" }), 3000);
  };

  const handleSubmit = async () => {
    const requiredFields = ["nom_produit", "code_isin", "societe_gestion", "type", "risque"];
    const missingFields = requiredFields.filter(field => !formValues[field]);

    if (missingFields.length > 0) {
      showToast("Veuillez remplir tous les champs obligatoires.", "error");
      return;
    }

    try {
      const produitData = {
        ...formValues,
        allocation_taux: formValues.allocation_taux || 0,
        allocation_actions: formValues.allocation_actions || 0,
        allocation_opc: formValues.allocation_opc || 0,
        allocation_convertibles: formValues.allocation_convertibles || 0,
        derives: formValues.derives || 0,
        sensibilite_taux: formValues.sensibilite_taux || 0,
      };

      const url = produitSelectionne
        ? `${BACKEND_URL}/api/produits/${produitSelectionne._id}`
        : `${BACKEND_URL}/api/produits`;

      await fetch(url, {
        method: produitSelectionne ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(produitData),
      });

      fetchProduits();
      showToast("✅ Produit enregistré avec succès !");
      setTimeout(() => handleClose(), 2000);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde du produit :", error);
      showToast("❌ Erreur lors de la sauvegarde.", "error");
    }
  };

  const handleClose = () => {
    setOpen(false);
    setFormValues({
      nom_produit: "",
      code_isin: "",
      societe_gestion: "",
      description: "",
      site_web: "",
      objectif: "",
      indice_reference: "",
      type: "",
      risque: "",
      duree_recommandee: "",
      depositaire: "",
      commentaires: "",
      lien_dic: "",
      allocation_taux: "",
      allocation_actions: "",
      allocation_opc: "",
      allocation_convertibles: "",
      derives: "",
      sensibilite_taux: "",
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="relative bg-white p-6 rounded-lg shadow-lg max-w-4xl w-full max-h-[95vh] overflow-y-auto">
    
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white p-6 rounded-lg shadow-lg max-w-4xl w-full"
        >
          {/* En-tête */}
          <div className="flex justify-between items-center border-b pb-3">
            <h2 className="text-xl font-semibold">{produitSelectionne ? "Modifier le produit" : "Ajouter un produit"}</h2>
            <button className="text-gray-500 hover:text-gray-800" onClick={handleClose}>✖</button>
          </div>

          {/* SECTION 1 - Informations Générales */}
          <h3 className="text-lg font-semibold mt-6">Informations Générales</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
            <input type="text" placeholder="Nom du Produit*" value={formValues.nom_produit} onChange={(e) => setFormValues({ ...formValues, nom_produit: e.target.value })} className="border rounded px-3 py-2" />
            <input type="text" placeholder="Code ISIN*" value={formValues.code_isin} onChange={(e) => setFormValues({ ...formValues, code_isin: e.target.value })} className="border rounded px-3 py-2" />
            <select
              value={formValues.societe_gestion}
              onChange={(e) => setFormValues({ ...formValues, societe_gestion: e.target.value })}
              className="border rounded px-3 py-2"
            >
              <option value="">-- Sélectionner une société --</option>
              {societes.map((nom, idx) => (
                <option key={idx} value={nom}>{nom}</option>
              ))}
            </select>
            <select
              value={formValues.type}
              onChange={(e) => setFormValues({ ...formValues, type: e.target.value })}
              className="border rounded px-3 py-2"
            >
              <option value="">-- Type de produit --</option>
              <option value="OPCVM">OPCVM</option>
              <option value="ETF">ETF</option>
              <option value="SICAV">SICAV</option>
              <option value="FCP">FCP</option>
              <option value="UCITS">UCITS</option>
              <option value="FIA">FIA</option>
              <option value="Fonds de capital investissement">Fonds de capital investissement</option>
              <option value="Fonds à formule">Fonds à formule</option>
              <option value="Fonds indiciels">Fonds indiciels</option>
              <option value="Fonds de fonds">Fonds de fonds</option>
              <option value="Fonds obligataires">Fonds obligataires</option>
              <option value="Fonds monétaires">Fonds monétaires</option>
            </select>
            <input type="number" placeholder="Risque*" value={formValues.risque} onChange={(e) => setFormValues({ ...formValues, risque: e.target.value })} className="border rounded px-3 py-2" />
            <input type="url" placeholder="Site Web" value={formValues.site_web} onChange={(e) => setFormValues({ ...formValues, site_web: e.target.value })} className="border rounded px-3 py-2" />
          </div>

          {/* SECTION 2 - Caractéristiques Financières */}
          <h3 className="text-lg font-semibold mt-6">Caractéristiques Financières</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
            <input type="text" placeholder="Objectif" value={formValues.objectif} onChange={(e) => setFormValues({ ...formValues, objectif: e.target.value })} className="border rounded px-3 py-2" />
            <input type="text" placeholder="Indice de Référence" value={formValues.indice_reference} onChange={(e) => setFormValues({ ...formValues, indice_reference: e.target.value })} className="border rounded px-3 py-2" />
            <input type="text" placeholder="Durée Recommandée" value={formValues.duree_recommandee} onChange={(e) => setFormValues({ ...formValues, duree_recommandee: e.target.value })} className="border rounded px-3 py-2" />
            <input type="text" placeholder="Dépositaire" value={formValues.depositaire} onChange={(e) => setFormValues({ ...formValues, depositaire: e.target.value })} className="border rounded px-3 py-2" />
            <input type="text" placeholder="Lien DIC" value={formValues.lien_dic} onChange={(e) => setFormValues({ ...formValues, lien_dic: e.target.value })} className="border rounded px-3 py-2" />
          </div>

          {/* SECTION 3 - Stratégie d’Investissement */}
          <h3 className="text-lg font-semibold mt-6">Stratégie d’Investissement</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
            <input type="number" placeholder="Allocation Taux" value={formValues.allocation_taux} onChange={(e) => setFormValues({ ...formValues, allocation_taux: e.target.value })} className="border rounded px-3 py-2" />
            <input type="number" placeholder="Allocation Actions" value={formValues.allocation_actions} onChange={(e) => setFormValues({ ...formValues, allocation_actions: e.target.value })} className="border rounded px-3 py-2" />
            <input type="number" placeholder="Allocation OPC" value={formValues.allocation_opc} onChange={(e) => setFormValues({ ...formValues, allocation_opc: e.target.value })} className="border rounded px-3 py-2" />
            <input type="number" placeholder="Allocation Convertibles" value={formValues.allocation_convertibles} onChange={(e) => setFormValues({ ...formValues, allocation_convertibles: e.target.value })} className="border rounded px-3 py-2" />
            <input type="number" placeholder="Dérivés" value={formValues.derives} onChange={(e) => setFormValues({ ...formValues, derives: e.target.value })} className="border rounded px-3 py-2" />
            <input type="number" placeholder="Sensibilité Taux" value={formValues.sensibilite_taux} onChange={(e) => setFormValues({ ...formValues, sensibilite_taux: e.target.value })} className="border rounded px-3 py-2" />
          </div>

          {/* SECTION 4 - Description et Commentaires */}
          <h3 className="text-lg font-semibold mt-6">Commentaires & Description</h3>
          <div className="grid grid-cols-1 gap-4 mt-2">
            <textarea placeholder="Description" value={formValues.description} onChange={(e) => setFormValues({ ...formValues, description: e.target.value })} className="border rounded px-3 py-2 min-h-[100px]" />
            <textarea placeholder="Commentaires" value={formValues.commentaires} onChange={(e) => setFormValues({ ...formValues, commentaires: e.target.value })} className="border rounded px-3 py-2 min-h-[100px]" />
          </div>

          {/* Boutons */}
          <div className="flex justify-between mt-6">
            <button onClick={handleClose} className="px-4 py-2 bg-gray-500 text-white rounded">Annuler</button>
            <button onClick={handleSubmit} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-all">
              {produitSelectionne ? "Modifier" : "Ajouter"}
            </button>
          </div>
        </motion.div>
        </DialogPanel>

        {/* ✅ Notification animée */}
        <AnimatePresence>
        {notification.show && (
          <motion.div
            initial={{ opacity: 0, x: 100, y: 50 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 100, y: 50 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-6 right-6 z-50 w-full max-w-sm"
          >
            <div
              className={`rounded-lg shadow-lg p-4 flex items-start gap-3 border-l-4 ${
                notification.type === "success"
                  ? "bg-green-50 border-green-500"
                  : "bg-red-50 border-red-500"
              }`}
            >
              <div className="shrink-0">
                {notification.type === "success" ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-500" />
                ) : (
                  <XCircleIcon className="h-6 w-6 text-red-500" />
                )}
              </div>
              <div className="flex-1 text-sm text-gray-700">
                <p className="font-semibold">
                  {notification.type === "success" ? "Succès" : "Erreur"}
                </p>
                <p className="text-sm">{notification.message}</p>
              </div>
            </div>
          </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Dialog>
  );
}